<script>
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useSecurityStore} from "@/stores/Security";
import axios from "axios";
import {ref, watch} from "vue";
import {useEvaluationDirectoryStore} from "@/stores/Evaluation/EvaluationDirectory";

export default {
    setup() {
        const {
            total,
            loading,
            filter,
        } = useSharedData();

        const {transformFilterValues} = useSharedMethod();

        const {$i18n, $router} = useTools();

        const search = ref(null);

        const evaluationDirectoryStore = useEvaluationDirectoryStore();
        const securityStore = useSecurityStore();

        const headers = [
            {
                text: "...",
                align: "left",
                sortable: true,
                value: "action",
                width: "0%"
            },
            {
                text: $i18n.t('common.code'),
                align: "left",
                sortable: true,
                value: "code",
                width: "0%"
            },
            {
                text: $i18n.t('common.skill'),
                align: "left",
                sortable: true,
                value: "catalogName",
                width: "0%"
            },
            {
                text: $i18n.t('common.comment'),
                align: "left",
                sortable: true,
                value: "notes"
            },
        ];

        const actions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'evaluation_skill_directories.edit', params: { id: item.id }})
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'evaluation_skill_directories.delete', params: { id: item.id }})
                },
            },
        ];

        function findAll() {
            loading.value = true;
            axios.get('/evaluationSkillDirectories', {params: transformFilterValues(filter.value)}).then((response) => {
                total.value = response.data.length;
                evaluationDirectoryStore.setEvaluationDirectories(response.data);
            }).finally(() => {
                loading.value = false;
            });
        }

        watch(() => filter.value, () => {
            findAll();
        }, {deep: true, immediate: true});

        return {
            evaluationDirectoryStore,
            securityStore,
            headers,
            actions,
            total,
            loading,
            filter,
            search,
            findAll,
        };
    },
}
</script>

<template>
    <div>
        <w-app
            :title="$t('module.evaluation_skill_directory')"
            show-total
            :total="total"
        >
            <template v-slot:rightToolbar>
                <w-btn-reload
                    :loading="loading"
                    @press="findAll"
                />
                <w-btn-add
                    :show="true"
                    @press="$router.push({ name: 'evaluation_skill_directories.add' })"
                />
            </template>
            <template v-slot:content>
                <v-container fluid>
                    <v-row dense>
                        <v-col cols="3">
                            <w-text-field
                                v-model="search"
                                :label="$t('common.search')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
                <w-data-table
                    :headers="headers"
                    :actions="actions"
                    :search="search"
                    :items="evaluationDirectoryStore.evaluationDirectories"
                    :total="total"
                    :loading="loading"
                    sort-by="catalogName"
                    show-expand
                    single-expand
                >
                    <template v-slot:[`item.code`]="{ item }">
                        <w-chip :text="item.code"/>
                    </template>
                    <template v-slot:[`item.catalogName`]="{ item }">
                        <w-inline-text bold :text="item.catalogName"/>
                    </template>
                    <template v-slot:[`item.levelsName`]="{ item }">
                        <w-chip
                            v-for="(level, index) in item.levelsName"
                            :key="`level-${index}`"
                            outlined
                            color="brown"
                            :text="level"
                        />
                    </template>
                    <template v-slot:expanded-item="{ item, headers }">
                        <td :colspan="2" style="background-color: #EEEEEE"></td>
                        <td :colspan="headers.length - 2" style="background-color: #EEEEEE">
                            <v-container fluid>
                                <v-simple-table dense class="elevation-5">
                                    <template v-slot:default>
                                        <thead>
                                        <tr>
                                            <th class="text-left">{{ $t('evaluation.level') }}</th>
                                            <th class="text-left">{{ $t('evaluation.mastery_reference') }}</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr
                                            v-for="(requirement, index) in item.requirements"
                                            :key="`requirement-${index}-${requirement.id}`"
                                        >
                                            <td class="text-left">
                                                <w-chip outlined :text="requirement.gravityName"/>
                                            </td>
                                            <td class="text-left">
                                                <span style="white-space: pre-wrap" v-html="requirement.notes.replace(/\n/g, '<br>')"></span>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                            </v-container>
                        </td>
                    </template>
                </w-data-table>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>